import React, { useEffect, useState } from "react";
import {
  Button,
  Content,
  Dialog,
  DialogContainer,
  Heading,
  Flex,
  Divider,
  TextArea,
  View,
  Text,
  Picker,
  Item,
} from "@adobe/react-spectrum";
import Close from "@spectrum-icons/workflow/Close";
import CustomDatePicker from "./CustomDatePicker/CustomDatePicker";
import CallbackSuccessCard from "./CallbackSuccessCard";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { rescheduleCallbackApi, rescheduleWebMeetingApi } from "../../api/session-api";
import { toast } from "react-toastify";
import Emitter from "../../emitter";
import { API_CALLBACK_DASHBOARD, TELEMETRY_ERROR } from "../../constants/events";


// Extend dayjs with plugins
dayjs.extend(timezone);
dayjs.extend(utc);

interface RescheduleCallbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  callbackData?: any;
  onReschedule?: () => void;
  timeslots: TimeslotOption[];
  userEmail?: string;
  userName?: string;
}

interface TimeslotOption {
  key: string;
  label: string;
  value: string;
}

const RescheduleCallbackDialog: React.FC<RescheduleCallbackDialogProps> = ({
  isOpen,
  onClose,
  callbackData,
  onReschedule,
  timeslots,
  userEmail,
  userName,
}) => {
  const isScheduledWeb = `${callbackData?.callbackType || ""}` === "SCHEDULED_WEB";
  const timezoneWithAMPM = callbackData?.callbackAttributes?.TIMEZONE;
  const timezone = timezoneWithAMPM?.split(" ")[0];
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedTimeslot, setSelectedTimeslot] = useState<string>("");
  const [rescheduleReason, setRescheduleReason] = useState<string>("");
  const [showSuccessCard, setShowSuccessCard] = useState(false);
  const [successData, setSuccessData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);


  const handleCancel = () => {
    // Reset form data
    setSelectedDate(null);
    setSelectedTimeslot("");
    setRescheduleReason("");
    setShowSuccessCard(false);
    setSuccessData(null);
    setIsLoading(false);
    onClose();
  };

  // Handle success card close
  const handleSuccessCardClose = () => {
    setShowSuccessCard(false);
    setSuccessData(null);
    handleCancel(); 
  };

  // Check if all required fields are filled
  const isFormValid = selectedDate && selectedTimeslot && rescheduleReason?.trim();

  const getWebMeetingId = () =>
    callbackData?.requestId || "";

  const getWebMeetingErrorMessage = (error: any, fallback: string) => {
    const body = error?.body;
    if (body?.fieldErrors) {
      return Object.values(body.fieldErrors).join(", ");
    }
    if (body?.error === "Invalid session") {
      return "Session expired. Please sign in again.";
    }
    if (body?.error === "Unable to reach web meeting service") {
      return "Unable to reach web meeting service. Please try again.";
    }
    return body?.message || error?.message || fallback;
  };

  const handleReschedule = async () => {
    if (!isFormValid) {
      return;
    }

    setIsLoading(true);
    
    const [startTime, endTime] = selectedTimeslot.split("  -  ");
    const [hours, minutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = (endTime || startTime).split(':').map(Number);
    const dateTimeString = selectedDate?.format("YYYY-MM-DD");
    const customerStartDateTime = dayjs.tz(dateTimeString, timezone)
      .hour(hours)
      .minute(minutes)
      .second(0);
    const customerEndDateTime = dayjs.tz(dateTimeString, timezone)
      .hour(endHours)
      .minute(endMinutes)
      .second(0);

    try {
      if (isScheduledWeb) {
        const rescheduleData = {
          webMeetingId: getWebMeetingId(),
          newMeetingStartTime: customerStartDateTime.utc().format(),
          newMeetingEndTime: customerEndDateTime.utc().format(),
          caseId: callbackData?.callbackAttributes?.CASEID,
          rescheduleReason: rescheduleReason.trim(),
          rescheduledByName: userName || undefined,
          rescheduledByEmail: userEmail || undefined,
          surface: "iHub",
        };

        const response = await rescheduleWebMeetingApi(rescheduleData);

        if (response?.success) {
          Emitter.emit(TELEMETRY_ERROR, {
            type: "notif",
            chan: TELEMETRY_ERROR,
            body: {
              type: API_CALLBACK_DASHBOARD,
              message: "Web meeting rescheduled successfully by " + userEmail,
              data: rescheduleData,
            },
          });

          setSuccessData({
            timezone,
            date: selectedDate?.format("ddd, MMM DD, YYYY"),
            timeslot: selectedTimeslot,
            reason: rescheduleReason.trim(),
          });
          setShowSuccessCard(true);

          if (onReschedule) {
            onReschedule();
          }
        } else {
          const fieldErrors = response?.fieldErrors
            ? Object.values(response.fieldErrors).join(", ")
            : "";
          toast.error(response?.message || fieldErrors || "Failed to reschedule web meeting");
          handleCancel();
        }
        return;
      }

      const epochTime = customerStartDateTime.unix();
      const rescheduleData = {
        contactId: callbackData?.contactId,
        callbackTime: epochTime,
        phoneNumber: callbackData?.phoneNumber,
        reason: rescheduleReason.trim(),
      };

      const response = await rescheduleCallbackApi(rescheduleData);
      
      if (response && response !== null && response.status === "SUCCESS") {
        Emitter.emit(TELEMETRY_ERROR, {
          type: "notif",
          chan: TELEMETRY_ERROR,
          body: {
            type: API_CALLBACK_DASHBOARD,
            message: "Callback Rescheduled successfully by " + userEmail,
            data : rescheduleData
          },
        });
        const successDetails = {
          timezone: timezone,
          date: selectedDate?.format("ddd, MMM DD, YYYY"),
          timeslot: selectedTimeslot,
          reason: rescheduleReason.trim(),
        };

        // console.log("Reschedule successful, showing success card:", successDetails);

        setSuccessData(successDetails);
        setShowSuccessCard(true);

        if (onReschedule) {
          onReschedule();
        }
      } else {
        console.error("Failed to reschedule callback - API returned null or error");
        toast.error("Failed to reschedule callback");
        handleCancel();
      }
    } catch (error) {
      console.error("Error rescheduling callback:", error);
      toast.error(
        isScheduledWeb
          ? getWebMeetingErrorMessage(error, "An error occurred while rescheduling the web meeting. Please try again.")
          : "An error occurred while rescheduling. Please try again."
      );
      handleCancel();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateSelect = (date: dayjs.Dayjs) => {
    setSelectedDate(date);
  };

  const getSlotStartTime = (slotKey: string) => {
    const [hours, minutes] = slotKey.split(":").map((part) => parseInt(part, 10));
    return dayjs().tz(timezone).hour(hours).minute(minutes).second(0).millisecond(0);
  };

  const getAvailableTimeslots = () => {
    if (!selectedDate) {
      return timeslots;
    }

    if (!selectedDate.isSame(dayjs().tz(timezone), "day")) {
      return timeslots;
    }

    const currentTime = dayjs().tz(timezone);
    let nextAvailableSlotSkipped = false;

    return timeslots.filter((slot) => {
      const slotStartTime = getSlotStartTime(slot.key);

      if (slotStartTime.isBefore(currentTime) || slotStartTime.isSame(currentTime, "minute")) {
        return false;
      }

      if (!nextAvailableSlotSkipped) {
        nextAvailableSlotSkipped = true;
        return false;
      }

      return true;
    });
  };

  const availableTimeslots = getAvailableTimeslots();

  useEffect(() => {
    if (!selectedTimeslot || !selectedDate) {
      return;
    }

    const isSelectedTimeslotAvailable = availableTimeslots.some(
      (slot) => slot.value === selectedTimeslot
    );

    if (!isSelectedTimeslotAvailable) {
      setSelectedTimeslot("");
    }
  }, [selectedDate, availableTimeslots, selectedTimeslot]);

  return (
    <>
      {/* Success Card */}
      <CallbackSuccessCard
        isOpen={showSuccessCard}
        onClose={handleSuccessCardClose}
        title={isScheduledWeb ? "Web meeting is rescheduled" : "Callback is rescheduled"}
        subtitle={isScheduledWeb ? "Your web meeting has been rescheduled with the below details" : "Your callback has been rescheduled with the below details"}
        details={successData}
        type="reschedule"
      />

      {/* Main Dialog */}
      <DialogContainer onDismiss={handleCancel}>
        {isOpen && !showSuccessCard && (
        <Dialog size="M">
          <Heading>
            <Flex alignItems="center" justifyContent="space-between">
              <Text>{isScheduledWeb ? "Reschedule Web Meeting" : "Reschedule Callback"}</Text>
              <span
                style={{ 
                  cursor: "pointer", 
                  fontSize: "24px", 
                  color: "#999",
                  padding: "8px",
                  borderRadius: "50%",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px"
                }}
                onClick={handleCancel}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                  e.currentTarget.style.color = '#666';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#999';
                }}
              >
                <Close />
              </span>
            </Flex>
          </Heading>
          <Divider />
          <Content>
            <View padding="size-200">
              <Flex direction="column" gap="size-200">
                {/* Question heading */}
                <Text UNSAFE_style={{ fontSize: "16px", fontWeight: "600", color: "#323232" }}>
                  {isScheduledWeb
                    ? "When would you like to reschedule the web meeting?"
                    : "When would you like to reschedule the callback?"}
                </Text>

                {/* Timezone display */}
                <View>
                  <Text UNSAFE_style={{ fontSize: "12px", marginBottom: "8px", display: "block" }}>
                    Customer Timezone
                  </Text>
                  <View 
                    UNSAFE_style={{ 
                      padding: "8px 12px", 
                      border: "1px solid #d3d3d3", 
                      borderRadius: "4px", 
                      backgroundColor: "#f5f5f5",
                      fontSize: "14px",
                      color: "#323232"
                    }}
                  >
                    <Text>{timezone || "-"}</Text>
                  </View>
                </View>

                {/* Date Picker */}
                <View>
                  <Text UNSAFE_style={{ fontSize: "12px", display: "block" }}>
                    Select date
                  </Text>
                  <CustomDatePicker
                    onDateSelect={handleDateSelect}
                    selectedDate={selectedDate || undefined}
                    width = "100%"
                  />
                </View>

                {/* Timeslot selection */}
                <View>
                  <Text UNSAFE_style={{ fontSize: "12px", marginBottom: "8px", display: "block" }}>
                    Select timeslot
                  </Text>
                  <Picker
                    label=""
                    selectedKey={availableTimeslots.find(slot => slot.value === selectedTimeslot)?.key || ""}
                    onSelectionChange={(key) => {
                      const selectedOption = availableTimeslots.find(slot => slot.key === key);
                      if (selectedOption) {
                        setSelectedTimeslot(selectedOption.value);
                      }
                    }}
                    width="100%"
                  >
                    {availableTimeslots.map((timeslot) => (
                      <Item key={timeslot.key}>{timeslot.label}</Item>
                    ))}
                  </Picker>
                </View>

                <View>
                  <Text UNSAFE_style={{ fontSize: "12px", marginBottom: "8px", display: "block" }}>
                    Reason for reschedule
                  </Text>
                  <TextArea
                    placeholder={
                      isScheduledWeb
                        ? "Please provide a reason for rescheduling this web meeting..."
                        : "Please provide a reason for rescheduling this callback..."
                    }
                    value={rescheduleReason}
                    onChange={setRescheduleReason}
                    width="100%"
                    height="size-1200"
                    UNSAFE_style={{ borderRadius: "4px" }}
                    maxLength={200}
                  />
                  {rescheduleReason.trim() && (
                    <Text
                      UNSAFE_style={{
                        fontSize: "12px",
                        color: "#4caf50",
                        marginTop: "4px",
                      }}
                    >
                      Reason provided
                    </Text>
                  )}
                </View>

                {/* Action buttons */}
                <Flex justifyContent="end" gap="size-100" marginTop="size-200">
                  <Button
                    variant="secondary"
                    onPress={handleCancel}
                    UNSAFE_style={{
                      backgroundColor: "#f5f5f5",
                      color: "#323232",
                      border: "1px solid #d3d3d3",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="cta"
                    onPress={handleReschedule}
                    isDisabled={!isFormValid || isLoading}
                    UNSAFE_style={{
                      backgroundColor: (isFormValid && !isLoading) ? "#0265dc" : "#d3d3d3",
                      color: "#ffffff",
                      cursor: (isFormValid && !isLoading) ? "pointer" : "not-allowed"
                    }}
                  >
                    {isLoading ? "Rescheduling..." : "Reschedule"}
                  </Button>
                </Flex>
              </Flex>
            </View>
          </Content>
        </Dialog>
        )}
      </DialogContainer>
    </>
  );
};

export default RescheduleCallbackDialog;
